/*
 * product-required-options.js
 *
 * Gates the Add-to-Cart button until required custom-option fields are filled in,
 * and powers the "Checkbox + conditional date" field type rendered by
 * blocks/_product-custom-options.liquid.
 *
 * Required choices use aria-disabled so an attempted add can explain and focus
 * the missing field. Submission is also checked at the form boundary.
 *
 * Scope: main product page only.
 *
 * Markers (emitted by the block):
 *   [data-required-option]        - a field that must be non-empty/valid
 *   [data-reveal-toggle]          - the checkbox that reveals a date field
 *   [data-reveal-date-wrap="ID"]  - wrapper hidden until its toggle is checked
 *   [data-reveal-date]            - the date input
 *   [data-required-when-checked]  - date input required only while its toggle is checked
 *   [data-option-group="ID"]      - pairs a toggle with its date input/wrapper
 */
(function () {
  'use strict';

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function todayPlus(days) {
    var d = new Date();
    d.setDate(d.getDate() + days);
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  // Recomputed lazily so a long-open tab past midnight still gets a correct min.
  function minDate() {
    return todayPlus(2);
  }

  // The Add-to-Cart button lives inside the field's own product form. Find it
  // relative to the form rather than guessing an id from section.id.
  function buttonFor(form) {
    if (!form || typeof form.querySelector !== 'function') return null;
    return form.querySelector('button[name="add"]') || form.querySelector('[type="submit"]');
  }

  function toggleFor(dateInput) {
    var group = dateInput.getAttribute('data-option-group');
    return document.querySelector('[data-reveal-toggle][data-option-group="' + group + '"]');
  }

  function normalize(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function normalizeOptionName(value) {
    return normalize(value)
      .replace(/^options\[/, '')
      .replace(/\]$/, '')
      .replace(/\s*\(option\)\s*$/, '')
      .trim();
  }

  function selectedVariantOption(sectionId, expectedName) {
    var picker = document.getElementById('variant-selects-' + sectionId);
    if (!picker) return '';
    var controls = picker.querySelectorAll('select, input[type="radio"]:checked');
    var wanted = normalizeOptionName(expectedName);

    for (var i = 0; i < controls.length; i++) {
      var actual = normalizeOptionName(controls[i].name);
      if (actual !== wanted && actual.indexOf(wanted + ' (') !== 0) continue;
      return controls[i].value;
    }
    return '';
  }

  function syncConditionalOptions() {
    var blocks = document.querySelectorAll('[data-hide-when-option-name][data-hide-when-option-value]');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var selectedValue = selectedVariantOption(
        block.getAttribute('data-product-section'),
        block.getAttribute('data-hide-when-option-name')
      );
      var shouldHide = normalize(selectedValue) === normalize(block.getAttribute('data-hide-when-option-value'));
      block.hidden = shouldHide;

      var fields = block.querySelectorAll('input, textarea, select, button');
      for (var j = 0; j < fields.length; j++) {
        var field = fields[j];
        if (!Object.prototype.hasOwnProperty.call(field.dataset, 'conditionalOriginalDisabled')) {
          field.dataset.conditionalOriginalDisabled = field.disabled ? 'true' : 'false';
        }

        if (shouldHide) {
          field.disabled = true;
          if (field.type === 'checkbox' || field.type === 'radio') field.checked = false;
          else if (field.tagName !== 'BUTTON') field.value = '';
        } else {
          field.disabled = field.dataset.conditionalOriginalDisabled === 'true';
        }
      }
    }
  }

  function syncReveals() {
    var toggles = document.querySelectorAll('[data-reveal-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var cb = toggles[i];
      var group = cb.getAttribute('data-option-group');
      var wrap = document.querySelector('[data-reveal-date-wrap="' + group + '"]');
      if (!wrap) continue;
      var dateInput = wrap.querySelector('[data-reveal-date]');
      if (cb.checked) {
        wrap.hidden = false;
      } else {
        wrap.hidden = true;
        if (dateInput) dateInput.value = '';
      }
    }
  }

  function setMinDates(min) {
    var dates = document.querySelectorAll('[data-reveal-date]');
    for (var i = 0; i < dates.length; i++) {
      dates[i].min = min;
    }
  }

  function fieldFilled(el) {
    if (el.value == null || String(el.value).trim() === '') return false;
    if (typeof el.checkValidity === 'function' && !el.checkValidity()) return false;
    return true;
  }

  function giftCardChosen(select) {
    var selected = select.options && select.options[select.selectedIndex];
    return Boolean(select.value && selected && !selected.hasAttribute('data-gift-card-placeholder'));
  }

  // Returns a Map of (form element -> isValid) for every form that has
  // required option fields.
  function computeValidity(min) {
    var result = new Map();

    function mark(form, ok) {
      if (!form) return;
      if (!result.has(form)) result.set(form, true);
      if (!ok) result.set(form, false);
    }

    var required = document.querySelectorAll('[data-required-option]');
    for (var i = 0; i < required.length; i++) {
      if (required[i].disabled) {
        mark(required[i].form, true);
        continue;
      }
      mark(required[i].form, fieldFilled(required[i]));
    }

    var giftCards = document.querySelectorAll('[data-required-gift-card-choice]');
    for (var card = 0; card < giftCards.length; card++) {
      if (!giftCards[card].disabled) mark(giftCards[card].form, giftCardChosen(giftCards[card]));
    }

    var dates = document.querySelectorAll('[data-required-when-checked]');
    for (var j = 0; j < dates.length; j++) {
      var d = dates[j];
      if (d.disabled) {
        mark(d.form, true);
        continue;
      }
      var cb = toggleFor(d);
      if (!cb || !cb.checked) {
        mark(d.form, true);
        continue;
      }
      mark(d.form, fieldFilled(d) && d.value >= min);
    }

    return result;
  }

  function firstMissing(form, min) {
    var cards = document.querySelectorAll('[data-required-gift-card-choice]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].form === form && !cards[i].disabled && !giftCardChosen(cards[i])) {
        return { field: cards[i], message: 'Please select a gift card, or choose No Gift card required.' };
      }
    }
    var required = document.querySelectorAll('[data-required-option]');
    for (var j = 0; j < required.length; j++) {
      if (required[j].form !== form || required[j].disabled || fieldFilled(required[j])) continue;
      var isGiftMessage = /gift card message/i.test(required[j].name || '');
      return { field: required[j], message: isGiftMessage ? 'Please enter your gift card message.' : 'Please complete the highlighted field.' };
    }
    var dates = document.querySelectorAll('[data-required-when-checked]');
    for (var k = 0; k < dates.length; k++) {
      if (dates[k].form !== form || dates[k].disabled) continue;
      var toggle = toggleFor(dates[k]);
      if (toggle && toggle.checked && (!fieldFilled(dates[k]) || dates[k].value < min)) {
        return { field: dates[k], message: 'Please complete the highlighted date.' };
      }
    }
    return null;
  }

  function clearFeedback(form) {
    if (!form) return;
    document.querySelectorAll('[data-required-options-invalid]').forEach(function (field) {
      if (field.form !== form) return;
      field.removeAttribute('data-required-options-invalid');
      field.removeAttribute('aria-invalid');
      var wrapper = field.closest('.img-dd');
      if (wrapper) {
        wrapper.removeAttribute('data-option-error');
        var toggle = wrapper.querySelector('.img-dd__toggle');
        if (toggle) toggle.removeAttribute('aria-invalid');
      } else {
        field.removeAttribute('data-option-error');
      }
    });
    var productForm = form.closest('product-form');
    if (productForm && productForm.dataset.requiredOptionsError === 'true') {
      delete productForm.dataset.requiredOptionsError;
      if (typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
    }
  }

  function explainMissing(form, issue) {
    var field = issue.field;
    var wrapper = field.closest('.img-dd');
    var focusTarget = wrapper ? wrapper.querySelector('.img-dd__toggle') || field : field;
    field.setAttribute('data-required-options-invalid', 'true');
    field.setAttribute('aria-invalid', 'true');
    (wrapper || field).setAttribute('data-option-error', 'true');
    if (focusTarget !== field) focusTarget.setAttribute('aria-invalid', 'true');
    var productForm = form.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.dataset.requiredOptionsError = 'true';
      productForm.handleErrorMessage(issue.message, true);
    }
    if (focusTarget.scrollIntoView) focusTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (focusTarget.focus) focusTarget.focus({ preventScroll: true });
  }

  function validateForm(form, showFeedback) {
    if (!form) return true;
    evaluate();
    var issue = firstMissing(form, minDate());
    if (!issue) return true;
    if (showFeedback) {
      clearFeedback(form);
      explainMissing(form, issue);
    }
    return false;
  }

  // Keep a missing choice clickable for an explanation; preserve stock gates.
  function setGate(btn, shouldDisable) {
    if (!btn) return;
    var packagingForm = btn.closest('[data-gift-packaging-gated]');
    var productForm = btn.closest('product-form');
    var sectionId = productForm && productForm.dataset.sectionId;
    var picker = sectionId && document.getElementById('variant-selects-' + sectionId);
    var variantAvailable = !picker || !picker.currentVariant || picker.currentVariant.available === true;
    var selector = sectionId && document.querySelector('[data-ll-gift-packaging-selector][data-section-id="' + sectionId + '"]');
    var canExplain = productForm && productForm.dataset.productAvailable === 'true' &&
      variantAvailable && (!selector || selector.dataset.llGpuRuntimeState !== 'unavailable');
    var targets = [btn];
    if (packagingForm) {
      targets = Array.prototype.filter.call(document.querySelectorAll('[data-gift-packaging-submit][data-section-id]'), function (button) {
        return button.dataset.sectionId === sectionId;
      });
    } else {
      document.querySelectorAll('button[form][type="submit"]').forEach(function (button) {
        if (button.form === btn.form && targets.indexOf(button) === -1) targets.push(button);
      });
    }

    targets.forEach(function (target) {
      if (shouldDisable) {
        target.dataset.optionGated = 'true';
        target.setAttribute('aria-disabled', 'true');
        if (canExplain) target.disabled = false;
      } else if (target.dataset.optionGated === 'true') {
        delete target.dataset.optionGated;
        var unavailable = picker && (!picker.currentVariant || picker.currentVariant.available !== true);
        target.disabled = packagingForm
          ? packagingForm.dataset.giftPackagingReady !== 'true'
          : Boolean(unavailable);
        target.toggleAttribute('aria-disabled', target.disabled);
      }
    });
  }

  function evaluate() {
    var min = minDate();
    setMinDates(min);
    syncConditionalOptions();
    syncReveals();
    computeValidity(min).forEach(function (valid, form) {
      setGate(buttonFor(form), !valid);
    });
  }

  function onEvent(e) {
    var t = e.target;
    if (t && t.closest && (t.closest('.product-options') || t.closest('variant-selects'))) {
      clearFeedback(t.form);
      evaluate();
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button[data-option-gated="true"]');
    if (!button || !button.form || validateForm(button.form, true)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  document.addEventListener('submit', function (event) {
    if (validateForm(event.target, true)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  document.addEventListener('input', onEvent);
  document.addEventListener('change', onEvent);
  document.addEventListener('shopify:section:load', evaluate);

  // On variant change the theme re-renders the buy buttons and clears the
  // disabled attribute (cart.js toggleAddButton); re-gate after it fires.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, function () { evaluate(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
  window.LuluLocalRequiredOptions = { validateForm: validateForm };
})();
